import {
  ClearIcon,
  Dialog,
  DialogContent,
  Hidden,
  Grid,
  styledComponent,
  Typography,
  Avatar,
  Button,
  Box,
  Rating,
  useTheme
} from '@hermes/web-components'
import { PropsWithChildren } from 'react'
import { ContactModalLayoutProps } from '../../../types/index'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import usePhoneViewPortHack from '../../../hooks/usePhoneViewPortHack'

export const StyledDialog = styledComponent(Dialog)(({ theme }) => ({
  display: 'flex',
  '& .MuiBackdrop-root': {
    background: theme.custom.DialogContent.background
  },
  '& .MuiDialog-paper': {
    height: '100%',
    margin: 0,
    maxHeight: 'none',
    borderRadius: 0,
    maxWidth: '600px',
    width: '100%',
    right: 0,
    position: 'fixed'
  },
  '& .MuiDialogContent-root': {
    width: '100%',
    padding: theme.custom.DialogContent.padding,
    overflow: 'hidden'
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100%',
      borderRadius: '16px 16px 0px 0px',
      marginTop: '10vh',
      bottom: 0,
      left: 0,
      // fix unexpected vh on IPhone
      height: 'calc(var(--vh, 1vh) * 95) !important'
    }
  }
}))

const CloseButton = styledComponent(Button)(({ theme }) => ({
  padding: '8px',
  minWidth: 'auto',
  borderRadius: '50%',
  color: theme.palette.text.black700,
  boxShadow: '0px 0px 7px 0px rgba(0, 0, 0, 0.10)',
  marginRight: '10px',
  width: 35,
  height: 35
}))

export const StyledScrollbar = styledComponent(Box)(({ theme }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: 'calc(100vh - 80px)',
  width: '100%',

  [theme.breakpoints.down('md')]: theme.custom.SpecialistPage.StyledScrollBar,

  '&::-webkit-scrollbar': {
    width: '6px'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.custom.scrollbarThumb.backgroundColor,
    borderRadius: '8px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: '#d5def9',
    borderRadius: '8px'
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#ccc'
  }
}))

const StyledDialogContent = styledComponent(DialogContent)({
  padding: '24px'
})

export const ContactModalLayout = ({
  isOpen,
  onClose,
  heading,
  children,
  avatar,
  name,
  rating,
  reviewsTotal,
  hideProfileHeader
}: PropsWithChildren<ContactModalLayoutProps>) => {
  const theme = useTheme()
  const profileAvatarAbbreviation = name?.[0].toUpperCase()
  const translate = useTranslateMessage()

  usePhoneViewPortHack()

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledDialogContent>
        <Grid container direction="column">
          <Hidden mdDown>
            <Grid container item direction="row" alignItems="center" pb="18px">
              <Grid item xs>
                <Typography fontFamily="Poppins" fontSize="23px" variant="h3" color="text.darkNavy" fontWeight={700}>
                  {heading}
                </Typography>
              </Grid>
              <CloseButton variant="ghost" onClick={onClose}>
                <ClearIcon />
              </CloseButton>
            </Grid>
          </Hidden>
          <Hidden mdUp>
            {hideProfileHeader ? (
              <Hidden mdUp>
                <Grid
                  container
                  mb={theme.custom.SpecialistPage.ProfileModalHeader.mb}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  wrap="nowrap"
                >
                  <Typography
                    mb="0px"
                    variant="h3"
                    color="text.darkNavy"
                    fontSize={theme.custom.SpecialistPage.ProfileHeaderTitle.fontSize}
                    fontFamily={theme.custom.SpecialistPage.ProfileHeaderTitle.fontFamily}
                    fontWeight={700}
                  >
                    {heading}
                  </Typography>
                  <CloseButton variant="ghost" onClick={onClose}>
                    <ClearIcon />
                  </CloseButton>
                </Grid>
              </Hidden>
            ) : (
              <>
                <Grid
                  mb="32px"
                  container
                  direction="row"
                  justifyContent="space-between"
                  wrap="nowrap"
                  alignItems="center"
                >
                  <Grid container direction="row" wrap="nowrap">
                    <Avatar src={avatar} sx={{ width: 48, height: 48, mr: '16px', borderRadius: '8px' }}>
                      {profileAvatarAbbreviation}
                    </Avatar>
                    <Grid container direction="column">
                      <Typography fontSize="16px" color="text.primary" lineHeight="24px" fontWeight={600}>
                        {name}
                      </Typography>
                      <Grid container>
                        <Grid container width="auto">
                          <Rating
                            size="small"
                            sx={{
                              fontSize: '15px'
                            }}
                            value={1}
                            max={1}
                            readOnly
                          />
                        </Grid>
                        <Grid item sx={{ paddingLeft: '4px !important' }}>
                          <Typography variant="body2" color="primary.dark" fontWeight="600">
                            {rating?.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item sx={{ paddingLeft: '8px !important' }}>
                          <Typography variant="body2" fontWeight="400" color="text.black900">
                            {reviewsTotal} {translate('reviews.reviews').toLowerCase()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <CloseButton variant="ghost" onClick={onClose}>
                    <ClearIcon />
                  </CloseButton>
                </Grid>
                <Typography mb="32px" variant="h3" color="text.darkNavy" fontWeight={700}>
                  {heading}
                </Typography>
              </>
            )}
          </Hidden>
          <StyledScrollbar>
            <Grid pr="8px" container wrap="nowrap" direction="column" item xs>
              {children}
            </Grid>
          </StyledScrollbar>
        </Grid>
      </StyledDialogContent>
    </StyledDialog>
  )
}
